'use client'

import React from 'react'
import { Button, useForm, useFormFields } from '@payloadcms/ui'

/**
 * Avviso sotto `featuredImage` nella edit view dei Post: compare SOLO quando l'articolo ha una
 * `seo.ogImage` valorizzata e la copertina vuota.
 *
 * PERCHÉ ESISTE. I due campi sono due picker `upload → media` identici a pochi centimetri l'uno
 * dall'altro — "Immagine di copertina" appena sopra il gruppo SEO, "Immagine condivisione social"
 * come primo campo dentro — e nessuno dei due, prima di questa modifica, diceva a cosa servisse.
 * Il post 60 (San Vittorino) è stato pubblicato con la foto nel campo sbagliato: og:image corretto,
 * quindi anteprima giusta condividendo il link su Facebook, e NESSUNA testata sulla pagina. Un
 * errore invisibile da dentro l'admin, perché da lì l'immagine "c'è".
 *
 * I due campi restano distinti di proposito: `ogImage` è l'override della card social, e un
 * fallback automatico della testata su `ogImage` toglierebbe la possibilità di avere una card
 * diversa dalla copertina. Qui si segnala l'incoerenza e si offre la copia esplicita, non la si
 * indovina in rendering.
 *
 * Il pulsante scrive solo nello stato del form: il salvataggio resta un gesto dell'utente, e con
 * esso gli hook (revalidation del tag `tenant:<id>`) che una UPDATE diretta si perderebbe.
 */

const styles = {
  actions: { display: 'flex', flexShrink: 0 },
  box: {
    alignItems: 'center',
    background: 'var(--theme-warning-50)',
    border: '1px solid var(--theme-warning-250)',
    borderRadius: 'var(--style-radius-s)',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
    justifyContent: 'space-between',
    marginTop: '0.5rem',
    padding: '0.75rem 1rem',
  },
  text: { color: 'var(--theme-warning-500)', fontSize: '0.85rem', lineHeight: 1.45, minWidth: 0 },
} satisfies Record<string, React.CSSProperties>

/** Il valore di un campo upload nello stato del form è l'id, ma può arrivare già popolato. */
const refId = (value: unknown): null | number | string => {
  if (typeof value === 'number' || typeof value === 'string') return value
  if (value && typeof value === 'object') {
    const id = (value as { id?: unknown }).id
    if (typeof id === 'number' || typeof id === 'string') return id
  }
  return null
}

export default function CoverMissingNotice() {
  const { dispatchFields, setModified } = useForm()

  // Tre selettori separati che tornano primitive: un unico selettore che costruisce un oggetto
  // rirenderebbe a ogni battuta in qualunque campo (use-context-selector confronta per identità).
  const cover = useFormFields(([fields]) => refId(fields.featuredImage?.value))
  const ogImage = useFormFields(([fields]) => refId(fields['seo.ogImage']?.value))
  const status = useFormFields(([fields]) => fields.status?.value)

  if (cover != null || ogImage == null) return null

  // Su una bozza è ancora un avvertimento; da "Programmato" in poi è un difetto già schedulato.
  const live = status === 'published' || status === 'scheduled'

  return (
    <div style={styles.box}>
      <div style={styles.text}>
        <strong>Copertina mancante.</strong> Questo articolo ha un&apos;immagine per la condivisione
        social ma nessuna copertina: la pagina viene renderizzata senza testata in cima.
        {live
          ? ' Lo è già adesso — condividendo il link la foto compare, aprendo l’articolo no.'
          : ' Succederà alla pubblicazione.'}
      </div>
      <div style={styles.actions}>
        <Button
          buttonStyle="secondary"
          margin={false}
          onClick={() => {
            dispatchFields({ type: 'UPDATE', path: 'featuredImage', value: ogImage })
            setModified(true)
          }}
          size="small"
        >
          Usa l&apos;immagine social come copertina
        </Button>
      </div>
    </div>
  )
}
